import { Link } from "react-router-dom";
const STUDIO_LOGO = "https://customer-assets.emergentagent.com/job_eamico-core/artifacts/jlqt1nkd_Coding%20and%20design%20harmony%20%282%29.png";
import { Sparkles, GitBranch, Globe, Terminal, Shield, Cpu, Users, Layers, Zap, Eye, Bug, Code2 } from "lucide-react";

const painPoints = [
  {
    icon: Sparkles,
    title: "AI that actually understands your codebase",
    text: "Not just autocomplete. Full-context AI across GPT-5.2, Claude, and Gemini — it reads your entire project, writes code, debugs errors, and generates commits. Like a senior engineer on call, 24/7.",
  },
  {
    icon: Terminal,
    title: "No more 'works on my machine'",
    text: "Every project gets its own cloud compute — from free starter instances to 16-core powerhouses. Full Linux terminal, zero local setup. Open a browser and start shipping.",
  },
  {
    icon: Globe,
    title: "Deploy without the deploy pipeline",
    text: "One click to Cloudflare, Vercel, or Netlify. Or use Quick Deploy for an instant shareable URL. Versioned snapshots, one-click rollback. No YAML files, no CI configs, no drama.",
  },
  {
    icon: GitBranch,
    title: "Git that doesn't make you cry",
    text: "Visual branches, inline diffs, blame annotations, merge conflict detection, and AI-generated commit messages. Everything you do in the terminal — but you can actually see what's happening.",
  },
  {
    icon: Eye,
    title: "See it live as you type it",
    text: "Side-by-side live preview with mobile and desktop viewports. Console output captured in real time. No build step, no refresh. Your changes are visible the moment you hit save.",
  },
  {
    icon: Bug,
    title: "Bugs fix themselves",
    text: "The AI Debugger watches your live preview, catches errors before you do, and suggests fixes with one-click apply. It's like having QA that never sleeps.",
  },
];

const moreFeatures = [
  { icon: Users, title: "Real-time collaboration", text: "Code together live. See cursors, edits, and even voice chat — all built in." },
  { icon: Shield, title: "Enterprise-grade security", text: "2FA, encrypted secrets, audit logging, and SOC 2 infrastructure. Your code is safe." },
  { icon: Cpu, title: "Scalable compute on demand", text: "From 2 vCPU free tier to 16-core machines. Upgrade any project in two clicks." },
  { icon: Layers, title: "GitHub, GitLab, Bitbucket", text: "Connect all your repos. One unified workspace. No more tab-switching." },
  { icon: Zap, title: "Command palette & shortcuts", text: "Ctrl+Shift+P your way through everything. Built for speed." },
  { icon: Code2, title: "Monaco Editor, supercharged", text: "The same engine as VS Code — with AI powers, cloud compute, and deploy built in." },
];

const StudioPage = ({ onOpenWaitlist }) => {
  return (
    <div className="min-h-screen bg-white" data-testid="studio-page">
      {/* Hero */}
      <div className="bg-[#f8f9fa] border-b border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src={STUDIO_LOGO} alt="Eamico Studio" className="w-10 h-10 rounded-xl" />
                <span className="text-[12px] font-bold text-[#ff9a4d] tracking-wide uppercase">Eamico Studio</span>
                <span className="text-[11px] font-semibold text-white bg-[#34a853] px-2.5 py-0.5 rounded-full uppercase tracking-wide">Alpha</span>
              </div>
              <h1 className="text-[32px] sm:text-[40px] lg:text-[48px] font-normal text-[#202124] leading-tight" data-testid="studio-title">
                Stop configuring.<br />Start building.
              </h1>
              <p className="mt-6 text-base sm:text-lg text-[#5f6368] leading-relaxed max-w-lg">
                The cloud IDE that replaces your local setup, your CI pipeline, your deployment scripts, and half your terminal aliases. Everything a developer needs — in one tab.
              </p>
              <p className="mt-3 text-[13px] font-medium text-[#34a853]">
                Currently in Alpha — Closed Beta launches June 1, 2026
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={onOpenWaitlist} className="inline-flex items-center px-6 py-3 bg-[#ff9a4d] hover:bg-[#e8872e] text-white text-[14px] font-medium rounded-md transition-colors shadow-sm" data-testid="studio-get-started">
                  Join waitlist
                </button>
                <Link to="/docs" className="inline-flex items-center px-6 py-3 border border-[#dadce0] hover:bg-white text-[14px] font-medium text-[#ff9a4d] rounded-md transition-colors" data-testid="studio-docs">
                  Read the docs
                </Link>
              </div>
            </div>

            {/* Hero image */}
            <div className="flex items-center justify-center lg:justify-end" data-testid="studio-preview">
              <img
                src="https://customer-assets.emergentagent.com/job_eamico-core/artifacts/mk5h25o4_CJ%20%281%29.png"
                alt="Developer working on Eamico Studio"
                className="w-full max-w-[420px] h-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main pain points */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="max-w-2xl mb-14">
          <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-normal text-[#202124] leading-tight">
            Built for developers who are{" "}
            <span className="text-[#ff9a4d]">tired of the tooling tax</span>
          </h2>
          <p className="mt-4 text-[15px] sm:text-base text-[#5f6368] leading-relaxed">
            You became a developer to build things — not to babysit webpack configs, debug Docker networks, or fight with deployment pipelines. Studio handles the infrastructure so you can focus on the code.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="studio-pain-points">
          {painPoints.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="p-8 rounded-xl border border-[#e8eaed] hover:border-[#dadce0] hover:shadow-sm transition-all" data-testid={`studio-pain-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-[#fff4eb] flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-[#ff9a4d]" />
                </div>
                <h3 className="text-[16px] font-medium text-[#202124] mb-2 leading-snug">{p.title}</h3>
                <p className="text-[14px] text-[#5f6368] leading-relaxed">{p.text}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* More features — compact */}
      <div className="bg-[#f8f9fa] border-t border-b border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] mb-10">And everything else you'd expect</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="studio-more-features">
            {moreFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="flex gap-4 p-5 bg-white rounded-xl border border-[#e8eaed]" data-testid={`studio-more-${i}`}>
                  <div className="w-9 h-9 rounded-lg bg-[#fff4eb] flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-[#ff9a4d]" />
                  </div>
                  <div>
                    <p className="text-[14px] font-medium text-[#202124]">{f.title}</p>
                    <p className="text-[13px] text-[#5f6368] mt-0.5 leading-relaxed">{f.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 text-center">
        <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124]">Your next project starts here</h2>
        <p className="mt-3 text-[15px] text-[#5f6368] max-w-md mx-auto">Free to start. No credit card. No local setup. Just open a browser and build.</p>
        <button onClick={onOpenWaitlist} className="inline-flex items-center mt-6 px-6 py-3 bg-[#ff9a4d] hover:bg-[#e8872e] text-white text-[14px] font-medium rounded-md transition-colors shadow-sm">
          Join waitlist
        </button>
      </div>
    </div>
  );
};

export default StudioPage;
